import React, {
  FC,
  createContext,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from 'react'
// import { Animated } from 'react-native'
import Animated, {
  useSharedValue,
  withSpring,
  useAnimatedStyle,
  WithSpringConfig,
} from 'react-native-reanimated'

import { Button } from './Button'
import { Element } from './Element'
import { Stack } from './Stack'

const SPRING_OPTIONS: WithSpringConfig = {
  mass: 1,
  damping: 25,
  stiffness: 250,
}

const NavigationSideBarContext = createContext({
  setIndex: (newIndex: number) => undefined as void,
})

export const NavigationSideBar: FC<{ selectedIndex: number; gap?: number }> = ({
  children,
  selectedIndex,
  gap = 0,
}) => {
  const hoverPosition = useSharedValue(selectedIndex * (9 + gap) * 4)

  const isPointerOver = useRef(false)

  const contextValue = useMemo(
    () => ({
      setIndex: (newIndex: number) => {
        hoverPosition.value = withSpring(
          newIndex * (9 + gap) * 4,
          SPRING_OPTIONS
        )
      },
    }),
    [selectedIndex]
  )

  useEffect(() => {
    if (!isPointerOver.current) {
      hoverPosition.value = withSpring(
        selectedIndex * (9 + gap) * 4,
        SPRING_OPTIONS
      )
    }
  }, [selectedIndex])

  const handlePointerEnter = useCallback(() => {
    isPointerOver.current = true
  }, [isPointerOver])

  const handlePointerLeave = useCallback(() => {
    isPointerOver.current = false

    hoverPosition.value = withSpring(
      selectedIndex * (9 + gap) * 4,
      SPRING_OPTIONS
    )
  }, [isPointerOver, selectedIndex])

  const backgroundStyle = useAnimatedStyle(() => {
    return {
      zIndex: 0,
      transform: [{ translateY: hoverPosition.value }],
    }
  }, [])

  return (
    <div
      style={{ cursor: 'pointer' }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <Element
        as={Animated.View}
        tokens={{
          backgroundColor: 'lightBlue.1',
          height: 9,
          borderRadius: 5,
          position: 'absolute',
          width: '100%',
        }}
        style={backgroundStyle}
      />

      <NavigationSideBarContext.Provider value={contextValue}>
        <Stack gap={gap}>{children}</Stack>
      </NavigationSideBarContext.Provider>
    </div>
  )
}

export const NavigationSideBarItem = ({
  children,
  index,
}: {
  children: React.ReactNode
  index: number
}) => {
  const sideBarContext = useContext(NavigationSideBarContext)

  const handleHoverIn = useCallback(() => {
    sideBarContext.setIndex(index)
  }, [sideBarContext])

  return (
    <Button alignItems='flex-start' variant='sideNav' onHoverIn={handleHoverIn}>
      {children}
    </Button>
  )
}
